import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import Pagination from '../../components/Pagination';
import {
  USER_STATS_PAGE_SIZE,
  USER_STATS_ROLE_OPTIONS,
  buildUserStatsExportParams,
  buildUserStatsPageParams,
  defaultUserStatsDateRange,
  formatReportAmount,
  formatReportCount,
  validateUserStatsDateRange,
} from './userStatisticsReportConstants';

const DEFAULT_RANGE = defaultUserStatsDateRange();

const EMPTY_FILTERS = {
  roleId: '1',
  startDateInput: DEFAULT_RANGE.startDateInput,
  endDateInput: DEFAULT_RANGE.endDateInput,
};

const TABLE_COLUMNS = [
  '日期',
  '註冊人數',
  '下單人數',
  '下單總額',
  '下單手續費總額',
  '盈虧總額',
  '充值人數',
  '充值總額',
  '首存金額',
  '首存人數',
  '二存金額',
  '二存人數',
  '三存金額',
  '三存人數',
  '提現人數',
  '提現總額',
  '實際提現總額',
  '帳戶總額',
  '系統加扣款',
];

const EMPTY_TOTAL = {
  registerNum: 0,
  orderNum: 0,
  totalOrderPrice: 0,
  totalOrderFeePrice: 0,
  totalProfit: 0,
  depositNum: 0,
  totalDeposit: 0,
  totalFirstDepositAmount: 0,
  firstDepositNum: 0,
  totalSecondDepositAmount: 0,
  secondDepositNum: 0,
  totalThirdDepositAmount: 0,
  thirdDepositNum: 0,
  withdrawalNum: 0,
  totalWithdrawal: 0,
  totalRealWithdrawal: 0,
  totalBalance: 0,
  systemModifyAmount: 0,
};

function renderTotalCell(total, key, formatter = formatReportCount) {
  return formatter(total?.[key]);
}

export default function UserStatisticsReportPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(EMPTY_TOTAL);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.memberDailyReportPageQuery(
        buildUserStatsPageParams(pageNo, nextFilters)
      );
      setRecords(data?.records || []);
      setSummary(data?.totalDto || EMPTY_TOTAL);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total || 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
      setSummary(EMPTY_TOTAL);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    const rangeError = validateUserStatsDateRange(filters.startDateInput, filters.endDateInput);
    if (rangeError) {
      setError(rangeError);
      return;
    }
    loadRecords(1);
  }

  function handleReset() {
    setFilters(EMPTY_FILTERS);
    setError('');
    loadRecords(1, EMPTY_FILTERS);
  }

  async function handleExport(exportCurrentPageOnly) {
    const rangeError = validateUserStatsDateRange(filters.startDateInput, filters.endDateInput);
    if (rangeError) {
      setError(rangeError);
      return;
    }
    setExporting(true);
    setError('');
    try {
      await adminApi.exportMemberDailyReportExcel(
        buildUserStatsExportParams(filters, exportCurrentPageOnly, page)
      );
    } catch (err) {
      setError(err.message || '匯出失敗');
    } finally {
      setExporting(false);
    }
  }

  function renderRow(row) {
    return (
      <tr key={row.datePeriod}>
        <td>{row.datePeriod || '—'}</td>
        <td>{formatReportCount(row.registerNum)}</td>
        <td>{formatReportCount(row.orderNum)}</td>
        <td>{formatReportAmount(row.totalOrderPrice)}</td>
        <td>{formatReportAmount(row.totalOrderFeePrice)}</td>
        <td>{formatReportAmount(row.totalProfit)}</td>
        <td>{formatReportCount(row.depositNum)}</td>
        <td>{formatReportAmount(row.totalDeposit)}</td>
        <td>{formatReportAmount(row.totalFirstDepositAmount)}</td>
        <td>{formatReportCount(row.firstDepositNum)}</td>
        <td>{formatReportAmount(row.totalSecondDepositAmount)}</td>
        <td>{formatReportCount(row.secondDepositNum)}</td>
        <td>{formatReportAmount(row.totalThirdDepositAmount)}</td>
        <td>{formatReportCount(row.thirdDepositNum)}</td>
        <td>{formatReportCount(row.withdrawalNum)}</td>
        <td>{formatReportAmount(row.totalWithdrawal)}</td>
        <td>{formatReportAmount(row.totalRealWithdrawal)}</td>
        <td>{formatReportAmount(row.totalBalance)}</td>
        <td>{formatReportAmount(row.systemModifyAmount)}</td>
      </tr>
    );
  }

  return (
    <div className="page">
      <div className="section-header">
        <h2>用戶統計報表</h2>
      </div>

      <form className="filter-bar" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="userStatsRoleId">帳號類型</label>
          <select
            id="userStatsRoleId"
            value={filters.roleId}
            onChange={(e) => updateFilter('roleId', e.target.value)}
          >
            {USER_STATS_ROLE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="userStatsStartDate">日期起</label>
          <input
            id="userStatsStartDate"
            type="date"
            value={filters.startDateInput}
            onChange={(e) => updateFilter('startDateInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="userStatsEndDate">日期迄</label>
          <input
            id="userStatsEndDate"
            type="date"
            value={filters.endDateInput}
            onChange={(e) => updateFilter('endDateInput', e.target.value)}
          />
        </div>

        <div className="filter-bar__actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset} disabled={loading}>
            重置
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(true)}
          >
            {exporting ? '匯出中…' : '匯出Excel（當前頁面）'}
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(false)}
          >
            {exporting ? '匯出中…' : '匯出Excel（全部資料）'}
          </button>
        </div>
      </form>

      {error ? <p className="form-error">{error}</p> : null}
      {loading ? <p className="page-status">載入中…</p> : null}

      <div className="table-wrap table-wrap--wide">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  暫無資料
                </td>
              </tr>
            ) : (
              records.map((row) => renderRow(row))
            )}
            {!loading && records.length > 0 ? (
              <tr className="data-table__summary-row">
                <td>匯總</td>
                <td>{renderTotalCell(summary, 'registerNum')}</td>
                <td>{renderTotalCell(summary, 'orderNum')}</td>
                <td>{renderTotalCell(summary, 'totalOrderPrice', formatReportAmount)}</td>
                <td>{renderTotalCell(summary, 'totalOrderFeePrice', formatReportAmount)}</td>
                <td>{renderTotalCell(summary, 'totalProfit', formatReportAmount)}</td>
                <td>{renderTotalCell(summary, 'depositNum')}</td>
                <td>{renderTotalCell(summary, 'totalDeposit', formatReportAmount)}</td>
                <td>{renderTotalCell(summary, 'totalFirstDepositAmount', formatReportAmount)}</td>
                <td>{renderTotalCell(summary, 'firstDepositNum')}</td>
                <td>{renderTotalCell(summary, 'totalSecondDepositAmount', formatReportAmount)}</td>
                <td>{renderTotalCell(summary, 'secondDepositNum')}</td>
                <td>{renderTotalCell(summary, 'totalThirdDepositAmount', formatReportAmount)}</td>
                <td>{renderTotalCell(summary, 'thirdDepositNum')}</td>
                <td>{renderTotalCell(summary, 'withdrawalNum')}</td>
                <td>{renderTotalCell(summary, 'totalWithdrawal', formatReportAmount)}</td>
                <td>{renderTotalCell(summary, 'totalRealWithdrawal', formatReportAmount)}</td>
                <td>{renderTotalCell(summary, 'totalBalance', formatReportAmount)}</td>
                <td>{renderTotalCell(summary, 'systemModifyAmount', formatReportAmount)}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={USER_STATS_PAGE_SIZE}
        onPageChange={(nextPage) => loadRecords(nextPage)}
      />
    </div>
  );
}
